import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Plus,
  Trash2,
  Send,
  Save,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  Copy,
  ArrowLeft,
  X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tooth numbers: upper 11-18, 21-28, lower 31-38, 41-48 (FDI notation)
const UPPER_RIGHT = [18,17,16,15,14,13,12,11];
const UPPER_LEFT = [21,22,23,24,25,26,27,28];
const LOWER_LEFT = [31,32,33,34,35,36,37,38];
const LOWER_RIGHT = [48,47,46,45,44,43,42,41];

const TREATMENTS = [
  { id: 'implant', name: 'Dental Implant', price: 300 },
  { id: 'crown', name: 'Crown', price: 100 },
  { id: 'veneer', name: 'Veneer', price: 50 },
  { id: 'root_canal', name: 'Root Canal', price: 50 },
  { id: 'extraction', name: 'Extraction', price: 50 },
  { id: 'whitening', name: 'Whitening', price: 100 },
  { id: 'filling', name: 'Filling', price: 30 },
  { id: 'cleaning', name: 'Cleaning', price: 50 },
  { id: 'bone_graft', name: 'Bone Graft', price: 500 },
  { id: 'hollywood_smile', name: 'Hollywood Smile', price: 200 },
];

// Tooth SVG Component
const ToothIcon = ({ number, isSelected, onClick, hasInterventions }) => {
  const isMolar = [16,17,18,26,27,28,36,37,38,46,47,48].includes(number);
  const isPremolar = [14,15,24,25,34,35,44,45].includes(number);
  const isCanine = [13,23,33,43].includes(number);
  const isUpper = number < 30;

  return (
    <button
      data-testid={`tooth-${number}`}
      onClick={() => onClick(number)}
      className={`relative group flex flex-col items-center transition-all ${isSelected ? 'scale-110' : 'hover:scale-105'}`}
    >
      <svg
        width="32" height="40"
        viewBox="0 0 32 40"
        className={`transition-all ${isSelected ? 'drop-shadow-md' : ''}`}
      >
        {/* Tooth shape */}
        {isMolar ? (
          <path
            d={isUpper
              ? "M6,8 C6,3 10,1 16,1 C22,1 26,3 26,8 L27,25 C27,30 23,32 16,32 C9,32 5,30 5,25 Z"
              : "M5,8 C5,3 9,1 16,1 C23,1 27,3 27,8 L26,25 C26,30 22,32 16,32 C10,32 6,30 6,25 Z"
            }
            fill={hasInterventions ? '#2dd4bf' : isSelected ? '#60a5fa' : '#e2e8f0'}
            stroke={isSelected ? '#2563eb' : hasInterventions ? '#14b8a6' : '#94a3b8'}
            strokeWidth="1.5"
          />
        ) : isPremolar ? (
          <path
            d={isUpper
              ? "M8,6 C8,2 11,1 16,1 C21,1 24,2 24,6 L25,27 C25,31 22,33 16,33 C10,33 7,31 7,27 Z"
              : "M7,6 C7,2 10,1 16,1 C22,1 25,2 25,6 L24,27 C24,31 21,33 16,33 C11,33 8,31 8,27 Z"
            }
            fill={hasInterventions ? '#2dd4bf' : isSelected ? '#60a5fa' : '#e2e8f0'}
            stroke={isSelected ? '#2563eb' : hasInterventions ? '#14b8a6' : '#94a3b8'}
            strokeWidth="1.5"
          />
        ) : isCanine ? (
          <path
            d="M10,5 C10,2 12,1 16,1 C20,1 22,2 22,5 L21,30 C21,34 19,36 16,36 C13,36 11,34 11,30 Z"
            fill={hasInterventions ? '#2dd4bf' : isSelected ? '#60a5fa' : '#e2e8f0'}
            stroke={isSelected ? '#2563eb' : hasInterventions ? '#14b8a6' : '#94a3b8'}
            strokeWidth="1.5"
          />
        ) : (
          <path
            d="M10,4 C10,2 12,1 16,1 C20,1 22,2 22,4 L22,28 C22,33 20,35 16,35 C12,35 10,33 10,28 Z"
            fill={hasInterventions ? '#2dd4bf' : isSelected ? '#60a5fa' : '#e2e8f0'}
            stroke={isSelected ? '#2563eb' : hasInterventions ? '#14b8a6' : '#94a3b8'}
            strokeWidth="1.5"
          />
        )}
        {/* Root indicator */}
        {hasInterventions && (
          <circle cx="16" cy="14" r="4" fill="#0d9488" opacity="0.6" />
        )}
      </svg>
      <span className={`text-[10px] font-mono mt-0.5 ${isSelected ? 'text-blue-600 font-bold' : 'text-slate-400'}`}>
        {number}
      </span>
    </button>
  );
};

// Dental Chart Component
const DentalChart = ({ selectedTeeth, onToothClick, teethData }) => {
  const hasInterventions = (num) => teethData[num]?.length > 0;
  
  return (
    <div data-testid="dental-chart" className="bg-white rounded-xl border border-slate-100 p-4 md:p-6">
      <div className="text-center mb-4">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Upper Jaw</p>
      </div>
      
      {/* Upper Teeth */}
      <div className="flex justify-center gap-0.5 md:gap-1 mb-1">
        {UPPER_RIGHT.map(n => (
          <ToothIcon key={n} number={n} isSelected={selectedTeeth.includes(n)} onClick={onToothClick} hasInterventions={hasInterventions(n)} />
        ))}
        <div className="w-2 md:w-4" />
        {UPPER_LEFT.map(n => (
          <ToothIcon key={n} number={n} isSelected={selectedTeeth.includes(n)} onClick={onToothClick} hasInterventions={hasInterventions(n)} />
        ))}
      </div>

      {/* Midline */}
      <div className="flex items-center gap-2 my-3">
        <div className="flex-1 h-px bg-slate-200" />
        <span className="text-[10px] text-slate-400 font-medium px-2">R</span>
        <div className="w-8 h-px bg-slate-300" />
        <span className="text-[10px] text-slate-400 font-medium px-2">L</span>
        <div className="flex-1 h-px bg-slate-200" />
      </div>

      {/* Lower Teeth */}
      <div className="flex justify-center gap-0.5 md:gap-1 mt-1">
        {LOWER_RIGHT.map(n => (
          <ToothIcon key={n} number={n} isSelected={selectedTeeth.includes(n)} onClick={onToothClick} hasInterventions={hasInterventions(n)} />
        ))}
        <div className="w-2 md:w-4" />
        {LOWER_LEFT.map(n => (
          <ToothIcon key={n} number={n} isSelected={selectedTeeth.includes(n)} onClick={onToothClick} hasInterventions={hasInterventions(n)} />
        ))}
      </div>

      <div className="text-center mt-4">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Lower Jaw</p>
      </div>

      {/* Legend */}
      <div className="flex justify-center gap-4 mt-4 text-xs text-slate-500">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-slate-200 border border-slate-300" />
          <span>Normal</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-blue-400 border border-blue-500" />
          <span>Selected</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-teal-400 border border-teal-500" />
          <span>Has Treatment</span>
        </div>
      </div>
    </div>
  );
};

// Intervention Panel for selected tooth
const InterventionPanel = ({ toothNumber, interventions, onAdd, onRemove, treatments }) => {
  const [selectedTreatment, setSelectedTreatment] = useState('');

  const handleAdd = () => {
    if (!selectedTreatment) return;
    const treatment = treatments.find(t => t.id === selectedTreatment);
    if (treatment) {
      onAdd(toothNumber, { treatment_id: treatment.id, treatment_name: treatment.name, price: treatment.price });
      setSelectedTreatment('');
    }
  };

  return (
    <Card className="border-blue-200 bg-blue-50/30">
      <CardHeader className="py-3 px-4">
        <CardTitle className="text-sm flex items-center justify-between">
          <span>Tooth #{toothNumber}</span>
          <Badge className="bg-blue-100 text-blue-700">{interventions.length} intervention{interventions.length !== 1 ? 's' : ''}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-3 space-y-2">
        {interventions.map((iv, idx) => (
          <div key={idx} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-slate-100">
            <div>
              <span className="text-sm font-medium">{iv.treatment_name}</span>
              <span className="text-sm text-accent ml-2 font-bold">${iv.price}</span>
            </div>
            <button onClick={() => onRemove(toothNumber, idx)} className="text-red-400 hover:text-red-600 transition">
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
        <div className="flex gap-2 mt-2">
          <Select value={selectedTreatment} onValueChange={setSelectedTreatment}>
            <SelectTrigger className="flex-1 h-8 text-xs bg-white">
              <SelectValue placeholder="Add treatment..." />
            </SelectTrigger>
            <SelectContent>
              {treatments.map(t => (
                <SelectItem key={t.id} value={t.id}>{t.name} - ${t.price}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={handleAdd} disabled={!selectedTreatment} className="h-8 text-xs">
            <Plus className="w-3 h-3 mr-1" />Add
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Estimates History
const EstimatesHistory = ({ estimates, onSend, onCopyLink }) => {
  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-slate-100 text-slate-600',
      sent: 'bg-blue-100 text-blue-700',
      viewed: 'bg-purple-100 text-purple-700',
      accepted: 'bg-green-100 text-green-700',
      declined: 'bg-red-100 text-red-700',
      modification_requested: 'bg-amber-100 text-amber-700'
    };
    return <Badge className={`text-[10px] ${styles[status] || 'bg-slate-100'}`}>{status?.replace(/_/g, ' ')}</Badge>;
  };

  return (
    <div className="space-y-2">
      {estimates.length === 0 && (
        <div className="text-center py-6 text-muted-foreground text-sm">No estimates created yet</div>
      )}
      {estimates.map(est => (
        <div key={est.estimate_id} className="bg-white rounded-lg border border-slate-100 p-3">
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="font-medium text-sm">{est.patient_name}</p>
              <p className="text-xs text-muted-foreground">{est.patient_email}</p>
            </div>
            {getStatusBadge(est.status)}
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span>{est.teeth?.length || 0} teeth</span>
              <span className="font-bold text-accent text-sm">${est.total_cost}</span>
            </div>
            <div className="flex items-center gap-1">
              {est.status === 'draft' && (
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onSend(est.estimate_id)}>
                  <Send className="w-3 h-3 mr-1" />Send
                </Button>
              )}
              {est.status !== 'draft' && (
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onCopyLink(est.estimate_id)}>
                  <Copy className="w-3 h-3 mr-1" />Link
                </Button>
              )}
            </div>
          </div>
          {est.patient_response_notes && (
            <div className="mt-2 p-2 bg-amber-50 rounded text-xs text-amber-800">
              <strong>Patient note:</strong> {est.patient_response_notes}
            </div>
          )}
          <p className="text-[10px] text-muted-foreground mt-1">{new Date(est.created_at).toLocaleString()}</p>
        </div>
      ))}
    </div>
  );
};

// Main Admin Dental Chart Tab
export default function AdminDentalChartTab() {
  const [view, setView] = useState('chart'); // chart, history
  const [selectedTeeth, setSelectedTeeth] = useState([]);
  const [teethData, setTeethData] = useState({}); // {toothNumber: [{treatment_id, treatment_name, price}]}
  const [patientName, setPatientName] = useState('');
  const [patientEmail, setPatientEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [estimates, setEstimates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [treatments, setTreatments] = useState(TREATMENTS);

  // Fetch dental pricing from admin settings
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/dental-pricing`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          if (data.treatments?.length) {
            setTreatments(data.treatments.filter(t => t.enabled).map(t => ({
              id: t.treatment_id,
              name: t.name,
              price: t.price
            })));
          }
        }
      } catch {}
    };
    fetchPricing();
    fetchEstimates();
  }, []);

  const fetchEstimates = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/dental-chart/estimates`, { credentials: 'include' });
      if (res.ok) setEstimates(await res.json());
    } catch {}
  };

  const handleToothClick = (number) => {
    setSelectedTeeth(prev => 
      prev.includes(number) ? prev.filter(n => n !== number) : [...prev, number]
    );
    if (!teethData[number]) {
      setTeethData(prev => ({ ...prev, [number]: [] }));
    }
  };

  const addIntervention = (toothNumber, intervention) => {
    setTeethData(prev => ({
      ...prev,
      [toothNumber]: [...(prev[toothNumber] || []), intervention]
    }));
  };

  const removeIntervention = (toothNumber, index) => {
    setTeethData(prev => ({
      ...prev,
      [toothNumber]: prev[toothNumber].filter((_, i) => i !== index)
    }));
  };

  const totalCost = Object.values(teethData).flat().reduce((sum, iv) => sum + iv.price, 0);

  const teethWithInterventions = Object.entries(teethData)
    .filter(([_, interventions]) => interventions.length > 0)
    .map(([toothNumber, interventions]) => ({
      tooth_number: parseInt(toothNumber),
      interventions
    }));

  const saveEstimate = async () => {
    if (!patientName.trim() || !patientEmail.trim()) {
      toast.error('Please enter patient name and email');
      return;
    }
    if (teethWithInterventions.length === 0) {
      toast.error('Please add at least one intervention');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/dental-chart/estimates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          patient_name: patientName,
          patient_email: patientEmail,
          teeth: teethWithInterventions,
          notes,
          total_cost: totalCost
        })
      });
      if (res.ok) {
        toast.success('Estimate saved!');
        resetChart();
        fetchEstimates();
      }
    } catch {
      toast.error('Failed to save estimate');
    } finally {
      setSaving(false);
    }
  };

  const resetChart = () => {
    setSelectedTeeth([]);
    setTeethData({});
    setPatientName('');
    setPatientEmail('');
    setNotes('');
  };

  const sendEstimate = async (estimateId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/dental-chart/estimates/${estimateId}/send`, {
        method: 'PUT',
        credentials: 'include'
      });
      if (res.ok) {
        toast.success('Estimate sent to patient');
        fetchEstimates();
      }
    } catch {
      toast.error('Failed to send estimate');
    }
  };

  const copyEstimateLink = (estimateId) => {
    const link = `${window.location.origin}/estimate/${estimateId}`;
    navigator.clipboard.writeText(link);
    toast.success('Link copied!');
  };

  return (
    <div data-testid="dental-chart-tab" className="space-y-4">
      {/* Tab Toggle */}
      <div className="flex items-center gap-2">
        <Button
          variant={view === 'chart' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setView('chart')}
          data-testid="dental-chart-create-btn"
        >
          <Plus className="w-4 h-4 mr-1" />Create Estimate
        </Button>
        <Button
          variant={view === 'history' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setView('history')}
          data-testid="dental-chart-history-btn"
        >
          <Clock className="w-4 h-4 mr-1" />History ({estimates.length})
        </Button>
      </div>

      {view === 'history' ? (
        <EstimatesHistory estimates={estimates} onSend={sendEstimate} onCopyLink={copyEstimateLink} />
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          {/* Chart - 2 columns */}
          <div className="xl:col-span-2 space-y-4">
            <DentalChart
              selectedTeeth={selectedTeeth}
              onToothClick={handleToothClick}
              teethData={teethData}
            />

            {/* Selected Teeth Interventions */}
            {selectedTeeth.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {selectedTeeth.map(num => (
                  <InterventionPanel
                    key={num}
                    toothNumber={num}
                    interventions={teethData[num] || []}
                    onAdd={addIntervention}
                    onRemove={removeIntervention}
                    treatments={treatments}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Sidebar - Patient Info & Summary */}
          <div className="space-y-4">
            <Card>
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-sm">Patient Information</CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-3">
                <div>
                  <Label className="text-xs">Patient Name</Label>
                  <Input
                    data-testid="patient-name-input"
                    value={patientName}
                    onChange={(e) => setPatientName(e.target.value)}
                    placeholder="John Doe"
                    className="h-8 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Patient Email</Label>
                  <Input
                    data-testid="patient-email-input"
                    value={patientEmail}
                    onChange={(e) => setPatientEmail(e.target.value)}
                    placeholder="patient@email.com"
                    className="h-8 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Notes</Label>
                  <Textarea
                    data-testid="estimate-notes-input"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Additional notes..."
                    rows={3}
                    className="text-sm"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Cost Summary */}
            <Card className={totalCost > 0 ? 'border-teal-200 bg-teal-50/30' : ''}>
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-sm">Cost Summary</CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                {teethWithInterventions.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Select teeth and add treatments</p>
                ) : (
                  <div className="space-y-2">
                    {teethWithInterventions.map(({ tooth_number, interventions }) => (
                      <div key={tooth_number} className="text-xs">
                        <span className="font-medium">Tooth #{tooth_number}:</span>
                        {interventions.map((iv, i) => (
                          <span key={i} className="text-muted-foreground ml-1">{iv.treatment_name} (${iv.price}){i < interventions.length - 1 ? ',' : ''}</span>
                        ))}
                      </div>
                    ))}
                    <div className="border-t border-slate-200 pt-2 mt-3">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-sm">Total</span>
                        <span className="text-lg font-bold text-teal-600" data-testid="total-cost">${totalCost}</span>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex gap-2">
              <Button
                data-testid="save-estimate-btn"
                onClick={saveEstimate}
                disabled={saving || teethWithInterventions.length === 0}
                className="flex-1"
              >
                <Save className="w-4 h-4 mr-1" />{saving ? 'Saving...' : 'Save Estimate'}
              </Button>
              <Button variant="outline" onClick={resetChart} className="text-xs">
                Clear
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
